import * as Sentry from '@sentry/react';

import {
  createGalleryImageUploadUrl,
  deleteGalleryImage,
  generateGalleryImageDescription,
  getGalleryImages,
  updateGalleryImage,
  uploadGalleryImage,
} from '../../api';
import {
  GALLERY_ACCEPTED_FILE_TYPES,
  GALLERY_MAX_FILE_SIZE,
  GALLERY_UPLOAD_FAILED_MESSAGE,
  readImageDimensions,
} from './images';

const DEFAULT_PER_PAGE = 20;

/**
 * Map a DonorSpring image record to the shape Unlayer's userUploads provider
 * expects. `source: 'user'` marks it as an end-user upload rather than stock.
 *
 * Note the provider shape has no description field, so descriptions are not
 * shown inside the editor — they exist for the gallery page and for choosing
 * images when generating email content.
 */
export const toUnlayerImage = (image) => ({
  id: image.imageId,
  location: image.url,
  width: image.width,
  height: image.height,
  contentType: image.contentType,
  size: image.size,
  source: 'user',
});

const isSupportedUpload = (file) => {
  if (!file) return 'No file was selected.';
  if (!Object.keys(GALLERY_ACCEPTED_FILE_TYPES).includes(file.type)) {
    return 'Only JPEG, PNG, GIF and WebP images are supported.';
  }
  if (file.size > GALLERY_MAX_FILE_SIZE) return 'File is larger than 5MB.';
  return null;
};

/**
 * Describe a newly uploaded image without ever disturbing the upload.
 *
 * An async function turns even a synchronous throw into a rejection its own catch
 * handles, so this can be fired and forgotten. Inlining it into the upload's try
 * block would let a failure here surface as a failed upload, which it is not.
 */
const describeInBackground = async (imageId) => {
  try {
    await generateGalleryImageDescription(imageId);
  } catch (err) {
    Sentry.captureException(err);
  }
};

/**
 * Point the editor's File Manager at the tenant's DonorSpring image library.
 *
 * Three registrations, and all three matter together:
 *  - `userUploads` provider  — the Uploads tab lists OUR library, not Unlayer's.
 *  - `image` callback        — uploads made INSIDE the editor go to OUR bucket.
 *  - `image:removed` callback — deletions inside the editor remove OUR record.
 *
 * Registering only the provider would leave the tenant with two disjoint
 * libraries: images uploaded in the editor would land in Unlayer's own storage
 * and never appear in the gallery (or be visible to the content agents).
 *
 * Images uploaded through the editor have no description, so they arrive in the
 * gallery flagged as "Needs description" — which is the intended nudge.
 *
 * Call from each editor's `onReady(unlayer)` handler. `onLibraryChanged` is an
 * optional hook so a surface showing the library alongside the editor can refresh.
 */
export const registerGalleryFileManager = (unlayer, { onLibraryChanged } = {}) => {
  if (!unlayer) return;

  unlayer.registerProvider('userUploads', async (params, done) => {
    const page = params?.page || 1;
    const perPage = params?.perPage || DEFAULT_PER_PAGE;

    try {
      const result = await getGalleryImages({
        page,
        perPage,
        search: params?.searchText || '',
      });

      done(result.images.map(toUnlayerImage), {
        hasMore: result.hasMore,
        page: result.page,
        perPage: result.perPage,
        total: result.total,
      });
    } catch (err) {
      Sentry.captureException(err);
      // `done` must always be called — the Uploads tab spins forever otherwise.
      done([], { hasMore: false, page, perPage, total: 0 });
    }
  });

  unlayer.registerCallback('image', async (file, done) => {
    const upload = file?.attachments?.[0];

    const validationError = isSupportedUpload(upload);
    if (validationError) {
      done({ error: validationError });
      return;
    }

    let uploadedImageId;

    try {
      const { width, height } = await readImageDimensions(upload);

      const { url, fields, image } = await createGalleryImageUploadUrl({
        filename: upload.name,
        contentType: upload.type,
        size: upload.size,
        width,
        height,
      });

      await uploadGalleryImage(url, fields, upload, (progress) => done({ progress }));
      await updateGalleryImage(image.imageId, { confirmed: true });
      uploadedImageId = image.imageId;

      done({ progress: 100, url: image.url });
      onLibraryChanged?.();
    } catch (err) {
      Sentry.captureException(err);
      done({ error: GALLERY_UPLOAD_FAILED_MESSAGE });
      return;
    }

    // Describe it too, so an image added from inside the editor is usable by the
    // content agents. Outside the try above so it can never turn a completed
    // upload into a reported failure; the editor has nowhere to show progress, and
    // the tenant can always write or regenerate the description in the gallery.
    void describeInBackground(uploadedImageId);
  });

  unlayer.registerCallback('image:removed', async (image, done) => {
    try {
      await deleteGalleryImage(image.id);
      onLibraryChanged?.();
    } catch (err) {
      Sentry.captureException(err);
    } finally {
      // Always acknowledge, or the builder leaves the image in a deleting state.
      done();
    }
  });
};
